import React, { use, useState } from 'react';
import ReactApexChart from "react-apexcharts";
import NoDataFound from '../../NoDataFound/NoDataFound';
import ChartFormatIndianNumber from "../../../Common/ChartFormatIndianNumber";
import NormalTable from '../../../../../utility/table/NormalTable.jsx';
import useThemeTextColor from '../../../../../utils/useThemeTextColor.jsx';
import ConfirmModal from '../../../../../utility/modals/ConfirmModal.jsx';
// import SkeletonLoader from "../../../../../utility/SkeletonLoader.jsx"; // ✅ your skeleton loader component
import { locationWiseData } from '../../../../../services/regionalLevelSales';
import { useTranslation } from 'react-i18next';
import PermissionControlledCard from '../../../../../common/PermissionControlledCard.jsx';
import { usePermissions } from '../../../../../context/PermissionContext.js';
import AreaTableModal from '../../../../../utility/modals/AreaTableModal.jsx';

const toTitleCase = (str) =>
  str?.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()) || 'Unknown';

const RegionalUnitsChart = ({
  downloadBtnPermission,
  chartData,
  level,
  filterData,
  selectedIds = [],
  onSelectionChange = () => {},
  isButtonDisabled = false,
}) => {
  const { t } = useTranslation();
  const textColor = useThemeTextColor();
  const borderColor = useThemeTextColor('--why-choose-card-border');
  const [locationData, setLocationData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [clickedData, setClickedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLocationName, setSelectedLocationName] = useState("");

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return <NoDataFound height={300} />;
  }
  const permission = usePermissions();

  // Sort by actual units
  const sortedData = [...chartData].sort((a, b) => b.actual_units - a.actual_units);
  const fullLabels = sortedData.map(item => toTitleCase(item.coast));
  

  // Chart series
  const series = [
    { name: "Actual Units", data: sortedData.map(item => item.actual_units) },
    { name: "Refund", data: sortedData.map(item => item.refund) },
    { name: "Cancelled", data: sortedData.map(item => item.cancelled_units) },
    { name: "Replacement", data: sortedData.map(item => item.replacement_units) },
  ];

  const handleBarClick = async (index) => {
    const selected = sortedData[index];
    if (!selected) return;
    setLoading(true);
    setShowModal(true);
    setClickedData(null); // reset old data

    try {
      const {
        productId = [],
        categoryId = [],
        parentproductId: parentProductIds = []
      } = filterData.selectedIds || {};

      const productIdsStr = productId.join(",");
      const categoryIdsStr = categoryId.join(",");
      const parentProductIdsStr = parentProductIds.join(",");

      const queryParams = new URLSearchParams({
        start_date: filterData.formattedStart,
        end_date: filterData.formattedEnd,
        filter: filterData.level,
        filter_value: selected.coast.toLowerCase(),
        parent_product_id:parentProductIdsStr,
        category_ids:categoryIdsStr,
        product_ids:productIdsStr
      });
      setSelectedLocationName(selected?.coast.toLowerCase())

      const responseData = await locationWiseData(queryParams);
      if (responseData?.data?.data && responseData?.data?.data.length > 0) {
        setLocationData(responseData?.data?.data);
      } else {
        setLocationData([]);
      }
    } catch (err) {
      console.error("Error fetching locations:", err);
      setLocationData([]); // fallback empty
    } finally {
      setLoading(false);
    }
  };

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      events: {
        dataPointSelection: (event, chartContext, config) => {
          if (config.dataPointIndex !== -1) {
            handleBarClick(config.dataPointIndex);
          }
        }
      }
    },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4 }
    },
    dataLabels: { enabled: false },
    stroke: { width: 1, colors: ["#fff"] },
    title: {
      text: t("regionalLevelSales.regionWiseSales"),
      align: "left",
      style: {
        fontSize: "20px",
        fontWeight: 600,
        color: "var(--heading-color)",
        fontFamily: "Quicksand"
      }
    },
    xaxis: {
      categories: fullLabels,
      labels: {
        style: { colors: textColor },
        formatter: (val) => val.toLocaleString("en-IN")
      }
    },
    tooltip: {
      y: { formatter: (val) => val.toLocaleString("en-IN") }
    },
    fill: { opacity: 1 },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      labels: { colors: textColor },
      markers: { radius: 12 }
    },
    grid: {
      borderColor: borderColor,
      strokeDashArray: 2,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    }
  };
const getSkeletonTableHTML = () => {
    // Pick headers based on tableId, or fallback to a default set
    const headers = ["NAME", "NET SALES", "ACTUAL UNITS", "RETURN UNITS"];

    const skeletonRow = `
      <tr>
        ${headers.map((_, idx) => {
          if (idx === 0) {
            return `
              <td>
                <div class="skeleton-product">
                  <div class="skeleton-image"></div>
                  <div class="skeleton-text-group">
                    <div class="skeleton-title"></div>
                    <div class="skeleton-subtitle"></div>
                  </div>
                </div>
              </td>`;
          }
          return "<td><div class='skeleton-box'></div></td>";
        }).join("")}
      </tr>`;

    return `
      <div class="table-toolbar">
        <div class="search-placeholder">
          <div class="skeleton-search-icon"></div>
          <div class="skeleton-search-input"></div>
        </div>
        <div class="export-icons">
          <div class="skeleton-icon"></div>
          <div class="skeleton-icon"></div>
        </div>
      </div>
      <table id="modalTable-${level}" class="display table" style="width:100%">
        <thead>
          <tr>
            ${headers.map(h => `<th class='skeleton-th'>${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${Array.from({ length: 10 }).map(() => skeletonRow).join("")}
        </tbody>
      </table>
    `;
  };
  const showTable = level === "city" || level === "postal" || level === "state";
  const tablePermission = level === "postal" ? permission?.data?.area_wise_insights?.pincode_section?.table_view : level === "state" ?  permission?.data?.area_wise_insights?.state_section?.table_view  : permission?.data?.area_wise_insights?.city_section?.table_view ;
  return (
    <div style={{ width: "100%" }}>
      {showTable ? (
        <PermissionControlledCard permission={tablePermission} colClass='mb-3'>
        <NormalTable
          defaultPageLength={5}
          tableId={`rrSales-${level}`}
          data={sortedData.map(item => ({
            id: item.id || item.coast,
            location: toTitleCase(item.coast),
            actual_units: item.actual_units,
            refund: item.refund,
            cancelled_units: item.cancelled_units,
            replacement_units: item.replacement_units,
            // rejected_units: item.rejected_units,
            // net_sales: item.net_sales,
          }))}
          enableSelection={false}
          filterData={filterData}
          idKey="id"
          columns={[
            { key: "location", label: "Location" },
            { key: "actual_units", label: "Actual Units", format: ChartFormatIndianNumber },
            { key: "refund", label: "Refund", format: ChartFormatIndianNumber },
            { key: "cancelled_units", label: "Cancelled", format: ChartFormatIndianNumber },
            { key: "replacement_units", label: "Replacement", format: ChartFormatIndianNumber },
            // { key: "rejected_units", label: "Rejected", format: ChartFormatIndianNumber }
          ]}
          downloadBtnPermission={downloadBtnPermission}
        />
        </PermissionControlledCard>
      ) : (
        <div id="chart">
         <PermissionControlledCard permission={permission?.data?.area_wise_insights?.region_section?.bar_graph } colClass='mb-3'>
          <ReactApexChart options={options} series={series} type="bar" height={350} />
          </PermissionControlledCard>
        </div>
      )}
      {showModal && (
          <>
           {loading ? (
            <div
              // dangerouslySetInnerHTML={{ __html: getSkeletonTableHTML() }}
            />
          ): locationData.length > 0 ? (
            // <PermissionControlledCard permission={permission?.data?.area_wise_insights?.region_section?.specific_details } colClass='mb-3'>
            <AreaTableModal show={showModal}
              title={selectedLocationName}
              data={locationData}
              categoryName={"Region"}
              onCancel={() => setShowModal(false)}
              permission={permission?.data?.area_wise_insights?.region_section?.specific_details}
              />

                        // </PermissionControlledCard>

              ) : (
                <div style={{ textAlign: "center", padding: "30px", fontWeight: "500" }} className='no-data-found'>
                  No data found
                </div>

          )}
          </>
          )}
      {/* <ConfirmModal
        show={showModal}
        title={`Details`}
        onCancel={() => setShowModal(false)}
      >
        {loading ? (
            <div
              dangerouslySetInnerHTML={{ __html: getSkeletonTableHTML() }}
            />
          ) : locationData.length > 0 ? (
            <NormalTable 
              defaultPageLength={5}
              tableId={`modalTable-${level}`}
              data={locationData}
              enableSelection={false}
              loading={false}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "30px", fontWeight: "500" }} className='no-data-found'>
              No data found
            </div>
          )}
      </ConfirmModal> */}
    </div>
  );
};

export default RegionalUnitsChart;
